import intl from "react-intl-universal";
import classNames from "classnames";
import React, {useCallback, useMemo, useState} from "react";
import {useSwipeable} from 'react-swipeable';
import {useAuthService, useDataClientService, useRootStore, useUserState} from "@stores/AppStateContext";
import {IDataClientService} from "../../../../../platform/services/data/DataClientService";
import {IAuthService} from "../../../../../platform/services/auth/AuthService";

import type {UserSessionState} from "@stores/UserSessionState";
import type {RootStoreModel} from "@stores/RootStore";
import {Notification} from "../../../../../API";

import {debounce} from "../../../../../utils/debounce";
import {Tooltip} from "../../../../components/Tooltip/Tooltip";
import {ArticleIcon} from "../../../../components/icons/ArticleIcon";
import {WorkspaceIcon} from "../../../../components/icons/WorkspaceIcon";
import {MentionIcon} from "../../../../components/icons/editor/MentionIcon";
import {QuizIcon} from "../../../../components/icons/QuizIcon";
import {PersonAdd} from "../../../../components/icons/PersonAdd";
import {KeyIcon} from "../../../../components/icons/KeyIcon";
import {UserAvatar} from "../../../../components/Avatar/Avatar";
import {NotificationAvatarBadge} from "./NotificationAvatarBadge";
import {DeleteIcon} from "../../../../components/icons/DeleteIcon";
import {NotificationCardBadge} from "./NotificationCardBadge";
import {formatDateShort} from "../../../../../utils/date";
import {MarkEmailUnread} from "../../../../components/icons/MarkEmailUnread";
import {MarkEmailRead} from "../../../../components/icons/MarkEmailRead";
import {hideNotification, setNotificationStatus} from "../../../../../actions/notification/setNotificationStatus";

import {NOTIFICATIONS} from "@stores/constants";
import {PAGES} from "@stores/ViewStore";
import {screenSizeProvider} from "@stores/ScreenSizeProvider";

import "./NotficationCard.css";

interface NotificationMeta {
    wsId?: string;
    workspaceName?: string;
    workspaceSlug?: string;
    itemId?: string;
    itemName?: string;
    quizId?: string;
    quizName?: string;
    userId?: string;
    userName?: string;
    role?: string;
    roleLabel?: string;
    roleName?: string;
    oldRoleName?: string;
    newRoleName?: string;
    scope?: 'workspace' | 'company';
    change?: 'assigned' | 'revoked' | 'changed' | 'permissions_changed' | 'deleted';
    reminder?: boolean;
}

function parseNotificationMeta(meta: Notification["meta"]): NotificationMeta {
    if (!meta) {
        return {};
    }

    if (typeof meta === 'string') {
        try {
            return JSON.parse(meta);
        } catch {
            return {};
        }
    }

    return meta as NotificationMeta;
}

function getTriggeredUserName(userState: UserSessionState, notification: Notification, meta: NotificationMeta) {
    if (notification.initiatorId) {
        const initiatorUser = userState?.usersDS.users.get(notification.initiatorId);
        if (initiatorUser?.name) {
            return initiatorUser.name;
        }
        if (initiatorUser?.email) {
            return initiatorUser.email;
        }
    }

    return meta.userName || intl.get('common.someone').d('Someone');
}

function membersNavigation(rootStore: RootStoreModel, wsSlug: string | null | undefined) {
    if (wsSlug) {
        return {
            href: rootStore.view.getFullUrl(PAGES.WORKSPACE_MEMBERS, {wsSlug}),
            open: () => rootStore.view.openPageByName(PAGES.WORKSPACE_MEMBERS, {wsSlug}),
        };
    }
    return {
        href: rootStore.view.getFullUrl(PAGES.SETTINGS_MEMBERS),
        open: () => rootStore.view.openPageByName(PAGES.SETTINGS_MEMBERS),
    };
}

function getNotificationNavigation(rootStore: RootStoreModel, userState: UserSessionState, notification: Notification, meta: NotificationMeta) {
    const relatedWs = meta.wsId ? userState.getWsById(meta.wsId) : null;
    const wsSlug = relatedWs?.slug || meta.workspaceSlug;

    switch (notification.type) {
        case NOTIFICATIONS.ACK_REQUIRE:
        case NOTIFICATIONS.ACK_CANCEL:
        case NOTIFICATIONS.ITEM_TRASH_ON:
        case NOTIFICATIONS.ITEM_TRASH_OFF:
        case NOTIFICATIONS.ITEM_ARCHIVE_ON:
        case NOTIFICATIONS.ITEM_ARCHIVE_OFF:
        case NOTIFICATIONS.DOC_PAGE_MENTION:
        case NOTIFICATIONS.DOC_COMMENT_MENTION:
        case NOTIFICATIONS.DOC_COMMENT_REPLAY:
        case NOTIFICATIONS.DOC_COMMENT_TREAD:
        case NOTIFICATIONS.DOC_COMMENT_RESOLVED:
            if (!meta.itemId) {
                return null;
            }
            return {
                href: rootStore.view.getFullUrl(PAGES.DOC_EDIT, {
                    id: meta.itemId,
                    itemName: meta.itemName,
                    wsSlug,
                }),
                open: () => rootStore.view.openPageByName(PAGES.DOC_EDIT, {
                    id: meta.itemId,
                    wsSlug,
                }),
            };
        case NOTIFICATIONS.QUIZ_REQUIRE:
        case NOTIFICATIONS.QUIZ_RETAKE:
            if (!meta.quizId) {
                return null;
            }
            return {
                href: rootStore.view.getFullUrl(PAGES.QUIZ_TAKE, {
                    id: meta.quizId,
                    wsSlug,
                }),
                open: () => rootStore.view.openPageByName(PAGES.QUIZ_TAKE, {
                    id: meta.quizId,
                    wsSlug,
                }),
            };
        case NOTIFICATIONS.QUIZ_CANCEL:
            return {
                href: rootStore.view.getFullUrl(PAGES.QUIZZES, {
                    wsSlug,
                }),
                open: () => rootStore.view.openPageByName(PAGES.QUIZZES, {
                    wsSlug,
                }),
            };
        case NOTIFICATIONS.USER_ROLE_CHANGE:
            if (meta.change === 'permissions_changed' || meta.change === 'deleted') {
                return {
                    href: rootStore.view.getFullUrl(PAGES.SETTINGS_ACCESSES),
                    open: () => rootStore.view.openPageByName(PAGES.SETTINGS_ACCESSES),
                };
            }
            return membersNavigation(rootStore, wsSlug);
        case NOTIFICATIONS.WS_ADD_USER:
        case NOTIFICATIONS.WS_REMOVE_USER:
            return membersNavigation(rootStore, wsSlug);
        case NOTIFICATIONS.USER_JOIN:
            return {
                href: rootStore.view.getFullUrl(PAGES.SETTINGS_MEMBERS),
                open: () => rootStore.view.openPageByName(PAGES.SETTINGS_MEMBERS),
            };
        default:
            return null;
    }
}

const getNotificationMsg = (rootStore: RootStoreModel, userState: UserSessionState, ntf: Notification) => {
    const meta = parseNotificationMeta(ntf.meta);
    const triggeredUser = getTriggeredUserName(userState, ntf, meta);
    const navigation = getNotificationNavigation(rootStore, userState, ntf, meta);
    const itemName = meta.itemName || intl.get('common.untitled_item').d('Untitled page');
    const workspaceName = meta.workspaceName || intl.get('common.workspace').d('Workspace');
    const quizName = meta.quizName || intl.get('quiz.common.quiz').d('test');
    // Hoist these fallbacks into locals: a nested intl.get().d() inside the params object of
    // intl.getHTML() confuses the i18n key extractor, which then writes the inner default
    // ("user"/"role") into the locale instead of the outer template.
    const userName = meta.userName || intl.get('common.user').d('user');
    const roleName = meta.roleName || meta.roleLabel || meta.role || intl.get('common.role').d('role');

    let msgReactElement: React.ReactNode;
    switch (ntf.type) {
        case NOTIFICATIONS.ACK_REQUIRE:
            msgReactElement = intl.getHTML('notification.requiresAcknowledge', {
                triggeredUser,
                itemName,
                itemHref: navigation?.href || '#',
            }).d(`<b>{triggeredUser}</b> requires you to acknowledge the Page <a href="{itemHref}">{itemName}</a>.`);
            break;
        case NOTIFICATIONS.ACK_CANCEL:
            msgReactElement = intl.getHTML('notification.cancelAcknowledge', {
                triggeredUser,
                itemName,
                itemHref: navigation?.href || '#',
            }).d(`<b>{triggeredUser}</b> cancelled the acknowledgment requirement for <a href="{itemHref}">{itemName}</a>.`);
            break;
        case NOTIFICATIONS.ITEM_TRASH_ON:
            msgReactElement = intl.getHTML('notification.itemTrashOn', {
                triggeredUser,
                itemName,
                itemHref: navigation?.href || '#',
            }).d(`<b>{triggeredUser}</b> moved your Page <a href="{itemHref}">{itemName}</a> to Trash.`);
            break;
        case NOTIFICATIONS.ITEM_TRASH_OFF:
            msgReactElement = intl.getHTML('notification.itemTrashOff', {
                triggeredUser,
                itemName,
                itemHref: navigation?.href || '#',
            }).d(`<b>{triggeredUser}</b> restored your Page <a href="{itemHref}">{itemName}</a> from Trash.`);
            break;
        case NOTIFICATIONS.ITEM_ARCHIVE_ON:
            msgReactElement = intl.getHTML('notification.itemArchiveOn', {
                triggeredUser,
                itemName,
                itemHref: navigation?.href || '#',
            }).d(`<b>{triggeredUser}</b> archived your Page <a href="{itemHref}">{itemName}</a>.`);
            break;
        case NOTIFICATIONS.ITEM_ARCHIVE_OFF:
            msgReactElement = intl.getHTML('notification.itemArchiveOff', {
                triggeredUser,
                itemName,
                itemHref: navigation?.href || '#',
            }).d(`<b>{triggeredUser}</b> restored your Page <a href="{itemHref}">{itemName}</a> from Archive.`);
            break;
        case NOTIFICATIONS.WS_ADD_USER:
            msgReactElement = intl.getHTML('notification.addInWorkspace', {
                triggeredUser,
                workspace: workspaceName,
                userName,
            }).d(`<b>{triggeredUser}</b> added <b>{userName}</b> to Workspace: <b>{workspace}</b>.`);
            break;
        case NOTIFICATIONS.WS_REMOVE_USER:
            msgReactElement = intl.getHTML('notification.removeWorkspace', {
                triggeredUser,
                workspace: workspaceName,
                userName,
            }).d(`<b>{triggeredUser}</b> removed <b>{userName}</b> from Workspace: <b>{workspace}</b>.`);
            break;
        case NOTIFICATIONS.DOC_PAGE_MENTION:
        case NOTIFICATIONS.DOC_COMMENT_MENTION:
            msgReactElement = intl.getHTML('notification.articleMention', {
                triggeredUser,
                itemName,
                itemHref: navigation?.href || '#',
            }).d(`<b>{triggeredUser}</b> mentioned you in <a href="{itemHref}">{itemName}</a>.`);
            break;
        case NOTIFICATIONS.DOC_COMMENT_REPLAY:
            msgReactElement = intl.getHTML('notification.commentReply', {
                triggeredUser,
                itemName,
                itemHref: navigation?.href || '#',
            }).d(`<b>{triggeredUser}</b> replied to you on <a href="{itemHref}">{itemName}</a>.`);
            break;
        case NOTIFICATIONS.DOC_COMMENT_TREAD:
            msgReactElement = intl.getHTML('notification.commentThread', {
                triggeredUser,
                itemName,
                itemHref: navigation?.href || '#',
            }).d(`<b>{triggeredUser}</b> added you to a discussion on <a href="{itemHref}">{itemName}</a>.`);
            break;
        case NOTIFICATIONS.DOC_COMMENT_RESOLVED:
            msgReactElement = intl.getHTML('notification.commentResolved', {
                triggeredUser,
                itemName,
                itemHref: navigation?.href || '#',
            }).d(`<b>{triggeredUser}</b> resolved a discussion on <a href="{itemHref}">{itemName}</a>.`);
            break;
        case NOTIFICATIONS.QUIZ_REQUIRE:
            msgReactElement = intl.getHTML('notification.takeQuiz', {
                triggeredUser,
                quizName,
                quizHref: navigation?.href || '#',
            }).d(`<b>{triggeredUser}</b> requires you to take <a href="{quizHref}">{quizName}</a>.`);
            break;
        case NOTIFICATIONS.QUIZ_RETAKE:
            msgReactElement = intl.getHTML('notification.retakeQuiz', {
                triggeredUser,
                quizName,
                quizHref: navigation?.href || '#',
            }).d(`<b>{triggeredUser}</b> requires you to retake <a href="{quizHref}">{quizName}</a>.`);
            break;
        case NOTIFICATIONS.QUIZ_CANCEL:
            msgReactElement = intl.getHTML('notification.cancelQuiz', {
                triggeredUser,
                quizName,
            }).d(`<b>{triggeredUser}</b> cancelled the assignment for {quizName}.`);
            break;
        case NOTIFICATIONS.USER_ROLE_CHANGE:
            if (meta.change === 'revoked') {
                msgReactElement = intl.getHTML('notification.roleRevoked', {
                    triggeredUser,
                    role: roleName,
                    workspace: workspaceName,
                }).d(`<b>{triggeredUser}</b> removed your {role} role in {workspace}.`);
            } else if (meta.change === 'assigned') {
                msgReactElement = intl.getHTML('notification.roleAssigned', {
                    triggeredUser,
                    role: roleName,
                    workspace: workspaceName,
                }).d(`<b>{triggeredUser}</b> assigned you the {role} role in {workspace}.`);
            } else if (meta.change === 'changed') {
                msgReactElement = intl.getHTML('notification.roleChanged', {
                    triggeredUser,
                    oldRole: meta.oldRoleName || roleName,
                    newRole: meta.newRoleName || roleName,
                    workspace: workspaceName,
                }).d(`<b>{triggeredUser}</b> changed your role in {workspace} from {oldRole} to {newRole}.`);
            } else if (meta.change === 'permissions_changed') {
                msgReactElement = intl.getHTML('notification.rolePermissionsChanged', {
                    triggeredUser,
                    role: roleName,
                }).d(`<b>{triggeredUser}</b> updated the permissions for the {role} role. Your access has changed.`);
            } else if (meta.change === 'deleted') {
                msgReactElement = intl.getHTML('notification.roleDeleted', {
                    triggeredUser,
                    role: roleName,
                }).d(`<b>{triggeredUser}</b> deleted the {role} role. Your access has been updated.`);
            } else {
                msgReactElement = intl.getHTML('notification.updatePermission', {
                    triggeredUser,
                    role: roleName,
                    workspace: workspaceName,
                }).d(`<b>{triggeredUser}</b> updated your permission level to {role}.`);
            }
            break;
        case NOTIFICATIONS.USER_JOIN:
            msgReactElement = intl.getHTML('notification.newUser', {
                triggeredUser,
                userName: meta.userName || triggeredUser,
            }).d(`<b>{userName}</b> joined the company.`);
            break;
        default:
            msgReactElement = ntf.type;
    }

    return (
        <div onClick={(event) => {
            const target = event.target as HTMLElement;
            if (target.tagName !== 'A' || !navigation) {
                return;
            }
            event.preventDefault();
            navigation.open();
        }}>
            {msgReactElement}
        </div>
    );
};

const updateServerReadStatusDebounced = debounce((
    dataClientService: IDataClientService,
    authService: IAuthService,
    notification: Notification,
    isRead: boolean
) => {
    setNotificationStatus(dataClientService, authService, notification, isRead ? 'unread' : 'read');
}, 250);

interface NotificationCardProps {
    notification: Notification;
}

export const NotificationCard = (
    {
        notification,
    }: NotificationCardProps
) => {
    const rootStore = useRootStore();
    const userState = useUserState();
    const dataClientService = useDataClientService();
    const authService = useAuthService();
    const [swipedRight, setSwipedRight] = useState(false);
    const [swipedLeft, setSwipedLeft] = useState(false);
    const [isRead, setIsRead] = useState(notification.status === 'read');
    const isMobile = screenSizeProvider.view !== 'desktop';

    const meta = useMemo(() => parseNotificationMeta(notification.meta), [notification.meta]);
    const relatedWs = meta.wsId ? userState?.getWsById(meta.wsId) : null;
    const headerTitle = relatedWs?.name || meta.workspaceName || intl.get('common.company').d('Company');
    const initiatorUser = notification.initiatorId && userState?.usersDS.users.get(notification.initiatorId);

    const onToggleRead = useCallback(
        () => {
            setIsRead(!isRead);
            updateServerReadStatusDebounced(
                dataClientService,
                authService,
                notification,
                isRead
            );
        },
        [authService, dataClientService, isRead, notification]
    );

    const handlers = useSwipeable({
        onSwipedLeft: () => {
            if (!swipedLeft && !swipedRight) {
                setSwipedLeft(true);
            }
            if (swipedRight) {
                setSwipedRight(false);
            }
        },
        onSwipedRight: () => {
            if (!swipedRight && !swipedLeft) {
                setSwipedRight(true);
            }
            if (swipedLeft) {
                setSwipedLeft(false);
            }
        },
        trackMouse: false,
    });

    return (
        <div className="NotificationCard_background">
            <DeleteIcon className={"NotificationCard_background_deleteIcon"} type={"danger"} size={24}
                        onClick={() => hideNotification(dataClientService, authService, notification)}/>
            {isRead
                ? <MarkEmailUnread className={"NotificationCard_background_markIcon"} size={24} onClick={onToggleRead}/>
                : <MarkEmailRead className={"NotificationCard_background_markIcon"} size={24} onClick={onToggleRead}/>
            }
            <div
                {...handlers}
                className={classNames({
                    "NotificationCard": true,
                    "NotificationCard--isRead": isRead,
                    "NotificationCard--swiped--left": swipedLeft,
                    "NotificationCard--swiped--right": swipedRight,
                })}>
                <div className="NotificationCard__AvatarPath">
                    {
                        initiatorUser
                            ? (
                                <NotificationAvatarBadge
                                    isRead={isRead}
                                    badgeContent={
                                        (() => {
                                            const Icon = getNotificationIcon(notification.type);
                                            return Icon
                                                ? <Icon size={14} className="NotificationCard__IconColor"/>
                                                : null;
                                        })()
                                    }
                                >
                                    <UserAvatar size={32} user={initiatorUser}/>
                                </NotificationAvatarBadge>
                            )
                            : null
                    }
                </div>

                <div className="Notification__message__block">
                    <div className={"Notification__message__headerBlock"}>
                        <div className="Notification__message__header body-small">{headerTitle}</div>
                        {isMobile &&
                            <div className="body-small Notification__Actions">
                                <span className="Notification__Date">
                                    {formatDateShort(new Date(notification.createdAt))}
                                </span>
                            </div>
                        }
                    </div>
                    <div className="Notification__message body-small">
                        {getNotificationMsg(rootStore, userState!, notification)}
                    </div>
                </div>
                {
                    !isMobile && (
                        <div className="Notification__rightPart">
                            <div className="body-small Notification__Actions">
                                <Tooltip title={intl.get('clear.common').d("Clear")}>
                                    <DeleteIcon size={16} className="Notification__DeleteIcon"
                                                onClick={() => hideNotification(dataClientService, authService, notification)}/>
                                </Tooltip>
                                <span className="Notification__Date">
                                    {formatDateShort(new Date(notification.createdAt))}
                                </span>
                            </div>
                            <NotificationCardBadge
                                isRead={isRead}
                                onClick={onToggleRead}
                            />
                        </div>
                    )
                }
            </div>
        </div>
    );
};

const getNotificationIcon = (type: string): React.ElementType | null => {
    switch (type) {
        case NOTIFICATIONS.ACK_REQUIRE:
        case NOTIFICATIONS.ACK_CANCEL:
        case NOTIFICATIONS.ITEM_TRASH_ON:
        case NOTIFICATIONS.ITEM_TRASH_OFF:
        case NOTIFICATIONS.ITEM_ARCHIVE_ON:
        case NOTIFICATIONS.ITEM_ARCHIVE_OFF:
            return ArticleIcon;
        case NOTIFICATIONS.WS_ADD_USER:
        case NOTIFICATIONS.WS_REMOVE_USER:
            return WorkspaceIcon;
        case NOTIFICATIONS.DOC_PAGE_MENTION:
        case NOTIFICATIONS.DOC_COMMENT_MENTION:
        case NOTIFICATIONS.DOC_COMMENT_REPLAY:
        case NOTIFICATIONS.DOC_COMMENT_TREAD:
        case NOTIFICATIONS.DOC_COMMENT_RESOLVED:
            return MentionIcon;
        case NOTIFICATIONS.QUIZ_REQUIRE:
        case NOTIFICATIONS.QUIZ_RETAKE:
        case NOTIFICATIONS.QUIZ_CANCEL:
            return QuizIcon;
        case NOTIFICATIONS.USER_ROLE_CHANGE:
            return KeyIcon;
        case NOTIFICATIONS.USER_JOIN:
            return PersonAdd;
        default:
            return null;
    }
};
